import Button from '@app/components/Button';
import ArrowForwardIcon from '@app/components/Icons/ArrowForwardIcon';
import H1 from '@app/components/typography/H1';
import FieldError from '@app/forms/components/FieldError';
import FormGroup from '@app/forms/components/FormGroup';
import Input from '@app/forms/components/Input';
import PasswordInput from '@app/forms/components/PasswordInput';
import { LoginContacts, LoginStep, SendTo } from '@app/pages/auth/Login/Login';
import { buttonGroup, contentBox, heading } from '@app/pages/auth/Login/Step1.sx';
import { Box, Grid } from '@mui/material';
import React, { Dispatch, SetStateAction } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useStep1 } from './useStep1';

interface Props {
  setStep: Dispatch<SetStateAction<LoginStep>>;
  setContacts: Dispatch<SetStateAction<LoginContacts>>;
  setSendTo: Dispatch<SetStateAction<SendTo>>;
}

const Step1 = ({ setStep, setContacts, setSendTo }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { isLoading, onFormSubmit, control, formErrors } = useStep1(setStep, setContacts, setSendTo);

  return (
    <Box>
      <H1 sx={heading}>{t('Sign in')}</H1>

      <form onSubmit={onFormSubmit}>
        <Grid container spacing={3} rowSpacing={2}>
          <Grid item xs={12}>
            <FormGroup label={t('Email')} required hasError={!!formErrors.email}>
              <Controller
                render={({ field }) => (
                  <Input placeholder="example@example.com" autoComplete="email" fullWidth {...field} />
                )}
                control={control}
                name="email"
              />

              <FieldError error={formErrors.email} />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <FormGroup label={t('Password')} required hasError={!!formErrors.password}>
              <Controller
                render={({ field }) => (
                  <PasswordInput
                    placeholder={t('Enter password')}
                    autoComplete="current-password"
                    fullWidth
                    {...field}
                  />
                )}
                control={control}
                name="password"
              />

              <FieldError error={formErrors.password} />
            </FormGroup>
          </Grid>
        </Grid>

        <Box sx={contentBox}>
          <Link to="/auth/restore-password" className="Link">
            {t('Forgot password?')}
          </Link>
        </Box>

        <Box sx={buttonGroup}>
          <Button type="submit" loading={isLoading} endIcon={!isLoading && <ArrowForwardIcon />}>
            {!isLoading && t('Sign in')}
          </Button>

          <Link to="/auth/registration">
            <Button variant="outlined" color="inherit">
              {t('Sign Up')}
            </Button>
          </Link>
        </Box>
      </form>
    </Box>
  );
};

export default Step1;
